import { CatalogItem, CatalogItemPrice } from "@common/types/catalogItem";
import { getActivePricesForCurrency } from "@common/helpers/catalogItemPrice";
import { getActivePriceForCurrency } from "@components/items/catalogItems.types";
import { NativeInvoiceLineRow } from "./nativeInvoiceFormTypes";

export interface CatalogItemSelection {
    catalogItemId: string;
    catalogItemPriceId: string | null;
    productOrService: string;
    description: string;
    unitAmount: number | null;
    // Item defaults propagated onto the line's own class/accrual/receivables
    // coding (see LineItemsSection.handleSelectItem). A null/empty value means
    // the item has no default for that dimension and the line's coding is
    // cleared on selection — matching how name/description/rate fully replace
    // the line's derived fields when a new item is selected.
    classIds: string[];
    accrualAccountId: string | null;
    receivablesAccountId: string | null;
}

/** A price row nested under an item that has more than one active price. */
export interface CatalogItemPricePickerRow {
    value: string;
    item: CatalogItem;
    price: CatalogItemPrice;
}

/**
 * One picker row per catalog item. `price` is the price a leaf selection
 * applies (the default when several exist). `priceRows` is set only when the
 * item has multiple active prices in the invoice currency, so the picker can
 * nest them instead of repeating the item once per price.
 */
export interface CatalogItemPickerRow {
    value: string;
    item: CatalogItem;
    price: CatalogItemPrice | null;
    priceRows?: CatalogItemPricePickerRow[];
}

const itemPickerValue = (itemId: string) => `item:${itemId}`;
const pricePickerValue = (priceId: string) => `price:${priceId}`;

export function buildCatalogItemPickerRows(
    items: CatalogItem[],
    currency: string,
): CatalogItemPickerRow[] {
    return items.map((item) => {
        const activePrices = getActivePricesForCurrency(item.prices, currency);
        const priceRows =
            activePrices.length > 1
                ? activePrices.map((price) => ({
                      value: pricePickerValue(price.id),
                      item,
                      price,
                  }))
                : undefined;
        return {
            value: itemPickerValue(item.id),
            item,
            price: activePrices[0] ?? null,
            priceRows,
        };
    });
}

export function selectionForCatalogItemPickerValue(
    rows: readonly CatalogItemPickerRow[],
    value: string,
    currency: string,
): CatalogItemSelection | null {
    for (const row of rows) {
        if (row.value === value) {
            return toCatalogItemSelection(row.item, currency, row.price);
        }
        const priceRow = row.priceRows?.find(
            (candidate) => candidate.value === value,
        );
        if (priceRow) {
            return toCatalogItemSelection(
                priceRow.item,
                currency,
                priceRow.price,
            );
        }
    }
    return null;
}

export function toCatalogItemSelection(
    item: CatalogItem,
    currency: string = "usd",
    selectedPrice?: CatalogItemPrice | null,
): CatalogItemSelection {
    const activePrice =
        selectedPrice === undefined
            ? getActivePriceForCurrency(item, currency)
            : selectedPrice;
    return {
        catalogItemId: item.id,
        catalogItemPriceId: activePrice ? activePrice.id : null,
        productOrService: item.name,
        description: item.description ?? "",
        unitAmount: activePrice ? activePrice.unitAmount : null,
        classIds: (item.classAssignments ?? []).map(
            (assignment) => assignment.classId,
        ),
        accrualAccountId: item.revenueAccountId,
        receivablesAccountId: item.receivablesAccountId,
    };
}

export function buildCatalogItemLinePatch(
    selection: CatalogItemSelection,
): Partial<NativeInvoiceLineRow> {
    return {
        catalogItemId: selection.catalogItemId,
        catalogItemPriceId: selection.catalogItemPriceId,
        productOrService: selection.productOrService,
        description: selection.description,
        ...(selection.unitAmount !== null
            ? { unitAmount: selection.unitAmount }
            : {}),
        classIds: selection.classIds,
        accrualAccountId: selection.accrualAccountId,
        receivablesAccountId: selection.receivablesAccountId,
    };
}
